import { httpClient } from './client';
import { logger } from '../services/logger';

import type { Customer, NewCustomer } from '../types/customer';

/**
 * Fetch customer autocomplete suggestions for a free-text search string.
 *
 * Pass an `AbortSignal` to make the request cancelable — callers that re-request
 * on each keystroke should abort the prior in-flight request and pass a fresh
 * signal so a stale response can't clobber a newer one.
 *
 * Returns `[]` for anything that isn't an array. A misconfigured baseURL can make this request
 * resolve against the SPA's own origin and come back as a 200 carrying `index.html`, and a truthy
 * non-array body would otherwise reach callers that immediately `.map` over it.
 */
export const getCustomerAutocomplete = async (searchString: string, signal?: AbortSignal): Promise<Customer[]> => {
  const response = await httpClient.get(`/customer/autocomplete/${encodeURIComponent(searchString)}`, { signal });

  if (!Array.isArray(response?.data)) {
    logger.logWarning('Customer autocomplete response body was not an array', {
      bodyType: typeof response?.data,
    });
    return [];
  }

  return response.data;
};

export const createCustomer = async (customer: NewCustomer): Promise<void> => {
  await httpClient.post('/customer', customer);
};
